import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { heroImages } from '@/mocks/data';

export const SITE_NAME = 'AllChinaBuy Spreadsheet';
export const SITE_ORIGIN = (import.meta.env.VITE_SITE_URL || '').replace(/\/+$/, '');
export const FAVICON_URL =
  'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/49750b7389edabeda6497a215e0702e5.png';
export const SITE_IMAGE = heroImages[0];

export interface SeoMeta {
  title: string;
  description: string;
  keywords: string;
  path: string;
  type?: 'website' | 'article';
  image?: string;
  noindex?: boolean;
}

export interface ArticleSeoData extends SeoMeta {
  datePublished: string;
  dateModified: string;
  author: string;
  category: string;
}

export const homeSeo: SeoMeta = {
  title: 'AllChinaBuy Spreadsheet — Find Products, Links & Buying Guides',
  description:
    'Discover organized AllChinaBuy spreadsheet resources, product links, buying guides, QC tips, shipping information and helpful tools — all in one place.',
  keywords:
    'AllChinaBuy spreadsheet, AllChinaBuy spreadsheet 2026, AllChinaBuy spreadsheet links, AllChinaBuy spreadsheet guide, AllChinaBuy products',
  path: '/',
  type: 'website',
  image: SITE_IMAGE,
};

export const articleSeoMap: Record<string, ArticleSeoData> = {
  'allchinabuy-spreadsheet-complete-guide': {
    title:
      'AllChinaBuy Spreadsheet Complete Guide: How to Find Products, Links & Buying Resources in 2026',
    description:
      'Learn how to use the AllChinaBuy spreadsheet to find products, links, and buying resources. This 2026 guide covers categories, QC tips, safety, and navigating buying agents.',
    keywords:
      'AllChinaBuy spreadsheet, AllChinaBuy spreadsheet guide, AllChinaBuy spreadsheet 2026, AllChinaBuy products, AllChinaBuy QC',
    path: '/article/allchinabuy-spreadsheet-complete-guide',
    type: 'article',
    image: SITE_IMAGE,
    datePublished: '2026-08-25',
    dateModified: '2026-08-25',
    author: 'Editorial Team',
    category: 'Guide',
  },
  'allchinabuy-review-2026': {
    title: 'AllChinaBuy Review 2026: Shipping Costs, QC Process & Platform Features',
    description:
      'Independent AllChinaBuy review for 2026 covering shipping costs, the QC process, warehouse features, and platform usability to help you decide if it is right for you.',
    keywords:
      'AllChinaBuy review, AllChinaBuy shipping cost, AllChinaBuy QC, AllChinaBuy warehouse, is AllChinaBuy legit',
    path: '/article/allchinabuy-review-2026',
    type: 'article',
    image: SITE_IMAGE,
    datePublished: '2026-08-20',
    dateModified: '2026-08-20',
    author: 'Editorial Team',
    category: 'Review',
  },
  'allchinabuy-shipping-cost-guide': {
    title: 'AllChinaBuy Shipping Cost Guide: Methods, Prices & Delivery Times Explained',
    description:
      'Understand AllChinaBuy shipping costs, methods, and delivery times in this practical guide covering package weight, destination, and how to reduce international shipping fees.',
    keywords:
      'AllChinaBuy shipping, AllChinaBuy shipping cost, AllChinaBuy shipping to USA, AllChinaBuy warehouse, AllChinaBuy delivery',
    path: '/article/allchinabuy-shipping-cost-guide',
    type: 'article',
    image: SITE_IMAGE,
    datePublished: '2026-08-15',
    dateModified: '2026-08-15',
    author: 'Editorial Team',
    category: 'Shipping',
  },
  'allchinabuy-safety-tips': {
    title: 'AllChinaBuy Safety Tips: Protect Yourself When Buying Online',
    description:
      'Essential AllChinaBuy safety tips to protect yourself when buying online — seller verification, QC photo analysis, secure payments, and avoiding common risks.',
    keywords:
      'AllChinaBuy safety, is AllChinaBuy legit, AllChinaBuy QC, AllChinaBuy payment, AllChinaBuy refund',
    path: '/article/allchinabuy-safety-tips',
    type: 'article',
    image: SITE_IMAGE,
    datePublished: '2026-08-10',
    dateModified: '2026-08-10',
    author: 'Editorial Team',
    category: 'Safety',
  },
};

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function applySeo(meta: SeoMeta) {
  document.title = meta.title;

  const canonical = SITE_ORIGIN ? `${SITE_ORIGIN}${meta.path}` : meta.path;

  upsertMeta('name', 'description', meta.description);
  upsertMeta('name', 'keywords', meta.keywords);
  upsertLink('canonical', canonical);
  upsertMeta('name', 'robots', meta.noindex ? 'noindex, nofollow' : 'index, follow');

  upsertMeta('property', 'og:title', meta.title);
  upsertMeta('property', 'og:description', meta.description);
  upsertMeta('property', 'og:url', canonical);
  upsertMeta('property', 'og:type', meta.type || 'website');
  upsertMeta('property', 'og:site_name', SITE_NAME);
  if (meta.image) upsertMeta('property', 'og:image', meta.image);

  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', meta.title);
  upsertMeta('name', 'twitter:description', meta.description);
  if (meta.image) upsertMeta('name', 'twitter:image', meta.image);
}

const fallbackSeo: SeoMeta = {
  title: 'AllChinaBuy Spreadsheet — Find Products, Links & Buying Guides',
  description:
    'Discover organized AllChinaBuy spreadsheet resources, product links, buying guides, QC tips, shipping information and helpful tools — all in one place.',
  keywords:
    'AllChinaBuy spreadsheet, AllChinaBuy spreadsheet 2026, AllChinaBuy spreadsheet links, AllChinaBuy spreadsheet guide',
  path: '/',
  type: 'website',
  noindex: true,
};

function resolveSeo(pathname: string): SeoMeta {
  if (pathname === '/' || pathname === '') return homeSeo;

  const articleMatch = pathname.match(/^\/article\/([^/]+)$/);
  if (articleMatch) {
    const seo = articleSeoMap[articleMatch[1]];
    if (seo) return seo;
  }

  return { ...fallbackSeo, path: pathname };
}

export function SeoManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    applySeo(resolveSeo(pathname));
  }, [pathname]);

  return null;
}